import { AxiosResponse, AxiosRequestConfig } from 'axios';
import client from 'api/client';
import {
    Template,
    TemplateFolder,
    TemplateVersion,
    TemplateFilters,
    BrandingSettings,
    PricingConfiguration,
} from 'views/retainerAgreement/types';

export interface TemplateListResponse {
    templates: Template[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
}

export interface TemplateCreatePayload {
    name: string;
    type:
        | 'visa-application'
        | 'service-agreement'
        | 'eligibility'
        | 'milestones'
        | 'engagement'
        | 'referral'
        | 'subcontractor'
        | 'msa'
        | 'revenue-sharing'
        | 'nda'
        | 'preferred-partner';
    country?: string;
    program?: string;
    assignedAgentId?: number | null;
    description?: string;
    tags?: string[];
    pricingModel: 'fixed' | 'hourly' | 'milestone' | 'hybrid';
    status?: 'draft' | 'published';
    content: string;
    htmlContent?: string;
    defaultCurrency?: string;
    defaultMilestones?: boolean;
    branding?: BrandingSettings;
    pricingConfig?: PricingConfiguration;
    hasLogo?: boolean;
    folderId?: number;
}

export type TemplateUpdatePayload = Partial<TemplateCreatePayload>;

export interface TemplateAssociationPayload {
    template_id: number | null;
}

export interface AgreementGenerationPayload {
    template_id?: number;
    custom_data?: Record<string, string>;
}

export interface AgreementGenerationResponse {
    html_content: string;
    placeholders_used: string[];
    placeholders_missing: string[];
    template?: Template;
}

export interface TemplatePreviewPayload {
    sample_data: Record<string, string>;
}

export interface TemplatePreviewResponse {
    html_content: string;
    placeholders_used: string[];
    placeholders_missing: string[];
}

export interface VersionComparePayload {
    version1_id: number;
    version2_id: number;
}

export interface VersionCompareResponse {
    changes: Array<{
        type: 'added' | 'removed' | 'unchanged';
        value: string;
    }>;
}

export interface BulkOperationPayload {
    template_ids: number[];
}

export interface FolderCreatePayload {
    name: string;
}

export interface MoveToFolderPayload {
    folder_id: number | null;
}

// Template CRUD Operations
export const templates = {
    get(
        filters?: TemplateFilters & { page?: number; per_page?: number },
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ data: TemplateListResponse }>> {
        const params = new URLSearchParams();
        if (filters) {
            if (filters.search) params.append('search', filters.search);
            if (filters.type && filters.type !== 'all') params.append('type', filters.type);
            if (filters.status && filters.status !== 'all') params.append('status', filters.status);
            if (filters.country) params.append('country', filters.country);
            if (filters.program) params.append('program', filters.program);
            if (filters.pricingModel && filters.pricingModel !== 'all')
                params.append('pricing_model', filters.pricingModel);
            if (filters.hasLogo !== 'all' && filters.hasLogo !== undefined)
                params.append('has_logo', String(filters.hasLogo));
            if (filters.page) params.append('page', String(filters.page));
            if (filters.per_page) params.append('per_page', String(filters.per_page));
        }
        const queryString = params.toString();
        return client.get<{ data: TemplateListResponse }>(
            `/retainer-templates${queryString ? `?${queryString}` : ''}`,
            customConfig
        );
    },

    getById(
        templateId: number,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ data: Template }>> {
        return client.get<{ data: Template }>(`/retainer-templates/${templateId}`, customConfig);
    },

    post(
        payload: TemplateCreatePayload,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ data: Template }>> {
        return client.post<{ data: Template }>('/retainer-templates', payload, customConfig);
    },

    put(
        templateId: number,
        payload: TemplateUpdatePayload,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ data: Template }>> {
        return client.put<{ data: Template }>(
            `/retainer-templates/${templateId}`,
            payload,
            customConfig
        );
    },

    delete(
        templateId: number,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ message: string }>> {
        return client.delete(`/retainer-templates/${templateId}`, customConfig);
    },
};

// Template Operations
export const templateOperations = {
    bulkDelete(
        payload: BulkOperationPayload,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ message: string; count: number }>> {
        return client.post('/retainer-templates/bulk-delete', payload, customConfig);
    },

    duplicate(
        templateId: number,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ data: Template }>> {
        return client.post<{ data: Template }>(
            `/retainer-templates/${templateId}/duplicate`,
            {},
            customConfig
        );
    },

    toggleFavorite(
        templateId: number,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ data: Template }>> {
        return client.post<{ data: Template }>(
            `/retainer-templates/${templateId}/toggle-favorite`,
            {},
            customConfig
        );
    },

    publish(
        templateId: number,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ data: Template }>> {
        return client.post<{ data: Template }>(
            `/retainer-templates/${templateId}/publish`,
            {},
            customConfig
        );
    },

    unpublish(
        templateId: number,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ data: Template }>> {
        return client.post<{ data: Template }>(
            `/retainer-templates/${templateId}/unpublish`,
            {},
            customConfig
        );
    },

    bulkPublish(
        payload: BulkOperationPayload,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ message: string }>> {
        return client.post('/retainer-templates/bulk-publish', payload, customConfig);
    },

    bulkUnpublish(
        payload: BulkOperationPayload,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ message: string }>> {
        return client.post('/retainer-templates/bulk-unpublish', payload, customConfig);
    },

    bulkDuplicate(
        payload: BulkOperationPayload,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ data: Template[] }>> {
        return client.post<{ data: Template[] }>(
            '/retainer-templates/bulk-duplicate',
            payload,
            customConfig
        );
    },

    markUsed(
        templateId: number,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ data: Template }>> {
        return client.post<{ data: Template }>(
            `/retainer-templates/${templateId}/mark-used`,
            {},
            customConfig
        );
    },
};

// Version Management
export const versions = {
    get(
        templateId: number,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ data: TemplateVersion[] }>> {
        return client.get<{ data: TemplateVersion[] }>(
            `/retainer-templates/${templateId}/versions`,
            customConfig
        );
    },

    getById(
        templateId: number,
        versionId: number,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ data: TemplateVersion }>> {
        return client.get<{ data: TemplateVersion }>(
            `/retainer-templates/${templateId}/versions/${versionId}`,
            customConfig
        );
    },

    compare(
        templateId: number,
        payload: VersionComparePayload,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<VersionCompareResponse>> {
        return client.post<VersionCompareResponse>(
            `/retainer-templates/${templateId}/versions/compare`,
            payload,
            customConfig
        );
    },
};

// Folder Management
export const folders = {
    get(customConfig?: AxiosRequestConfig): Promise<AxiosResponse<{ data: TemplateFolder[] }>> {
        return client.get<{ data: TemplateFolder[] }>('/retainer-templates/folders', customConfig);
    },

    post(
        payload: FolderCreatePayload,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ data: TemplateFolder }>> {
        return client.post<{ data: TemplateFolder }>(
            '/retainer-templates/folders',
            payload,
            customConfig
        );
    },

    delete(
        folderId: number,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ message: string }>> {
        return client.delete(`/retainer-templates/folders/${folderId}`, customConfig);
    },
};

// Utility Endpoints
export const utility = {
    getRecentlyUsed(
        limit?: number,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ data: Template[] }>> {
        const params = limit ? `?limit=${limit}` : '';
        return client.get<{ data: Template[] }>(
            `/retainer-templates/recently-used${params}`,
            customConfig
        );
    },

    getFavorites(customConfig?: AxiosRequestConfig): Promise<AxiosResponse<{ data: Template[] }>> {
        return client.get<{ data: Template[] }>('/retainer-templates/favorites', customConfig);
    },

    getForSelection(
        filters?: { country?: string; program?: string; type?: string; limit?: number },
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ data: Template[] }>> {
        const params = new URLSearchParams();
        if (filters?.country) params.append('country', filters.country);
        if (filters?.program) params.append('program', filters.program);
        if (filters?.type) params.append('type', filters.type);
        if (filters?.limit) params.append('limit', String(filters.limit));
        const queryString = params.toString();
        return client.get<{ data: Template[] }>(
            `/retainer-templates/for-selection${queryString ? `?${queryString}` : ''}`,
            customConfig
        );
    },

    getMeta(
        filters?: { country?: string; program?: string; type?: string },
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ data: Array<{ id: number; name: string; pricingModel: string }> }>> {
        const params = new URLSearchParams();
        if (filters?.country) params.append('country', filters.country);
        if (filters?.program) params.append('program', filters.program);
        if (filters?.type) params.append('type', filters.type);
        const queryString = params.toString();
        return client.get<{ data: Array<{ id: number; name: string; pricingModel: string }> }>(
            `/retainer-templates/meta${queryString ? `?${queryString}` : ''}`,
            customConfig
        );
    },
};

// Application Template Integration
export const applicationTemplates = {
    associate(
        applicationId: number,
        payload: TemplateAssociationPayload,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ message: string; template_id: number | null }>> {
        return client.put(`/application/${applicationId}/template`, payload, customConfig);
    },

    get(
        applicationId: number,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ data: Template | null }>> {
        return client.get<{ data: Template | null }>(
            `/application/${applicationId}/template`,
            customConfig
        );
    },

    generateAgreement(
        applicationId: number,
        payload?: AgreementGenerationPayload,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<{ data: AgreementGenerationResponse }>> {
        return client.post<{ data: AgreementGenerationResponse }>(
            `/application/${applicationId}/generate-agreement`,
            payload || {},
            customConfig
        );
    },
};

// Template Preview
export const preview = {
    generate(
        templateId: number,
        payload: TemplatePreviewPayload,
        customConfig?: AxiosRequestConfig
    ): Promise<AxiosResponse<TemplatePreviewResponse>> {
        return client.post<TemplatePreviewResponse>(
            `/retainer-templates/${templateId}/preview-generation`,
            payload,
            customConfig
        );
    },
};

// Move template to folder
export function moveToFolder(
    templateId: number,
    payload: MoveToFolderPayload,
    customConfig?: AxiosRequestConfig
): Promise<AxiosResponse<{ data: Template }>> {
    return client.put<{ data: Template }>(
        `/retainer-templates/${templateId}/folder`,
        payload,
        customConfig
    );
}
